/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021-2024 (original work) Open Assessment Technologies SA ;
 */

//keep a map of all ongoing readers, to abort them if necessary
const readers = new Map();

/**
 * The upload service factory for base 64.
 * The file data is encoded in BASE 64 inside the response.
 * @param {string} interactionId - to identify an interaction
 * @returns {Object} the service
 */
export default function base64UploadServiceFactory(interactionId) {
    return {
        name: 'base64',

        /**
         * Converts the file data to base 64
         * @param {Object} fileData
         * @param {File} fileData.data
         * @returns {Promise<Object>} resolves with the response value data
         */
        upload({ data: file }) {
            if (file instanceof File) {
                return new Promise((resolve, reject) => {
                    this.cancel();

                    const reader = new FileReader();
                    readers.set(interactionId, reader);
                    reader.addEventListener('load', () => {
                        // some browser returns only with `data:`, when the file is empty
                        resolve({
                            data: reader.result.replace(/^data:(.*?base64,)?/, ''),
                            mime: file.type,
                            name: file.name
                        });
                    });

                    reader.addEventListener('abort', () => resolve(null));
                    reader.addEventListener('error', error => reject(error));
                    reader.readAsDataURL(file);
                }).finally(() => readers.delete(interactionId));
            }
            return Promise.resolve(null);
        },

        /**
         * Cancel the current conversion
         * @returns {boolean} true if an ongoing upload has been canceled
         */
        cancel() {
            if (readers.has(interactionId)) {
                const previousReader = readers.get(interactionId);
                if (previousReader instanceof FileReader && previousReader.readyState === 1) {
                    //reader state is LOADING
                    previousReader.abort();
                    return Promise.resolve(true);
                }
            }
            return Promise.resolve(false);
        },

        /**
         * Get the response base type
         * @returns {string}  the base type
         */
        getBaseType() {
            return 'file';
        }
    };
}
